// The net weight the tipping form shows under the gross, before the tap.
//
// The SERVER works out every tip's net: tipping_log's trigger calls
// calculate_tipping_net_weight → resolve_bin_tare_kg(delivery, NULL), which
// takes the delivery's own custom bin type, then this business's row for its
// container, then the grower's own tare (growers.bin_tare_kg), and only then
// Config → bin_tare_kg — 37 kg when that is unset (mig 20260701235600:81-160).
// The form used to preview gross − N × the Config tare (25 kg when unset), so a
// CHEP delivery or a grower with its own bins showed one net and saved another
// (F02 defect D1). The operator checks the net before tapping; it has to be
// the net that is saved.
//
// So the preview asks the server for that same number — one read per opened
// delivery (authenticated holds EXECUTE; SECURITY INVOKER, so RLS applies) —
// instead of re-implementing the rungs. ⚠️ Do NOT swap this for
// src/lib/tareResolver.ts: it has no grower rung, so it would still preview
// the wrong net for a grower with an override.
//
// A failed read is never stated as fact (readState): the net is then shown as
// an amber ≈ estimate on the Config tare, and not at all when the Config tare
// did not load either — a number nobody configured is a guess.

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { withReadTimeout } from '@/lib/saveTimeout'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { useBinTare } from '@/lib/binConfig'
import { readState } from '@/lib/readState'
import { tipNetWeight } from '@/lib/tippingMath'

/** The tare (kg per bin) the server will apply to a tip off this delivery. */
function useDeliveryTare(deliveryId: string) {
  const tenantId = useActiveTenantId()
  return useQuery({
    // resolve_bin_tare_kg reads current_tenant_id() (container row, Config).
    queryKey: ['delivery-tare', deliveryId, tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await withReadTimeout(
        supabase.rpc('resolve_bin_tare_kg', { p_delivery_id: deliveryId, p_bin_id: null }),
        'Tipping: resolve_bin_tare_kg',
      )
      if (error) throw error
      const kg = Number(data)
      if (data == null || !Number.isFinite(kg) || kg < 0) {
        throw new Error('resolve_bin_tare_kg returned no tare')
      }
      return kg
    },
  })
}

/**
 * The net the server will save for this tip, or undefined (not checked) while
 * there is no usable delivery tare (loading, failed read, offline with nothing
 * cached): Tip stays available and the server checks. Shares the preview's
 * query, so it costs no extra read. Feeds canSubmitTip.
 */
export function useTipNetKg(deliveryId: string, gross: number, binQty: number): number | null | undefined {
  const tareQ = useDeliveryTare(deliveryId)
  if (readState(tareQ) !== 'usable' || tareQ.data === undefined) return undefined
  return tipNetWeight(gross, binQty, tareQ.data)
}

function UsableNet({ gross, binQty, tare }: { gross: number; binQty: number; tare: number }) {
  const net = tipNetWeight(gross, binQty, tare) ?? 0
  if (net <= 0) {
    // The server refuses a net of zero or below, so say so before the tap.
    return (
      <div className="text-caption mt-1 space-y-0.5">
        <p className="text-fg-muted">
          Net weight: <span className="text-danger font-semibold tabular-nums">{net.toFixed(1)} kg</span>
          <span className="text-muted ml-1">(tare {binQty} × {tare} kg)</span>
        </p>
        <p className="text-danger" role="alert">
          Net is below zero. For {binQty} bin{binQty !== 1 ? 's' : ''} the gross must be more than {binQty * tare} kg.
        </p>
      </div>
    )
  }
  return (
    <p className="text-caption text-fg-muted mt-1">
      Net weight: <span className="text-success font-semibold tabular-nums">{net.toFixed(1)} kg</span>
      <span className="text-muted ml-1">(tare {binQty} × {tare} kg)</span>
    </p>
  )
}

export default function TipNetPreview({ deliveryId, gross, binQty }: {
  deliveryId: string
  gross: number
  binQty: number
}) {
  const tareQ = useDeliveryTare(deliveryId)
  const config = useBinTare()
  const state = readState(tareQ)

  if (!(gross > 0)) return null

  if (state === 'usable' && tareQ.data !== undefined) return <UsableNet gross={gross} binQty={binQty} tare={tareQ.data} />

  if (state === 'loading') {
    return (
      <p className="text-caption text-fg-muted mt-1">
        Net weight: <span className="text-muted">reading this delivery's tare…</span>
      </p>
    )
  }

  // unread — the read failed, or the device is offline with nothing cached.
  if (config.isFallback) {
    return (
      <div className="text-caption mt-1 space-y-0.5">
        <p className="text-fg-muted">Net weight: <span className="text-muted">not shown</span></p>
        <p className="text-warning">
          Couldn't read this delivery's tare. The net is worked out when the tip saves — check it in Tips from this delivery on this run.
        </p>
      </div>
    )
  }
  const estimate = tipNetWeight(gross, binQty, config.tareKg) ?? 0
  return (
    <div className="text-caption mt-1 space-y-0.5">
      <p className="text-fg-muted">
        Net weight: <span className="text-warning font-semibold tabular-nums">≈ {estimate.toFixed(1)} kg</span>
        <span className="text-muted ml-1">(estimate · tare {binQty} × {config.tareKg} kg from Config)</span>
      </p>
      <p className="text-warning">
        Couldn't read this delivery's tare, so this uses the Config tare. The saved net may differ — check it in Tips from this delivery on this run.
      </p>
    </div>
  )
}
